import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Shield, AlertTriangle, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const CompliancePage = () => {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newRule, setNewRule] = useState({
    name: '',
    rule_type: 'min_break_hours',
    value: 10,
    is_blocking: false,
    description: ''
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await api.get('/compliance-rules');
      setRules(res.data);
    } catch (error) {
      toast.error('Failed to load compliance rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/compliance-rules', newRule);
      toast.success('Compliance rule created');
      setCreateDialogOpen(false);
      setNewRule({
        name: '',
        rule_type: 'min_break_hours',
        value: 10,
        is_blocking: false,
        description: ''
      });
      fetchRules();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  const handleToggleActive = async (ruleId, currentState) => {
    try {
      await api.put(`/compliance-rules/${ruleId}`, { is_active: !currentState });
      toast.success(`Rule ${!currentState ? 'enabled' : 'disabled'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const ruleTypeLabels = {
    min_break_hours: 'Minimum Break Between Shifts (hours)',
    max_shift_hours: 'Maximum Shift Length (hours)',
    max_weekly_hours: 'Maximum Weekly Hours',
    required_qualification: 'Required Qualification',
    staffing_ratio: 'Staffing Ratio'
  };

  const ruleTypeIcons = {
    min_break_hours: Clock,
    max_shift_hours: Clock,
    max_weekly_hours: Calendar,
    required_qualification: Shield,
    staffing_ratio: Shield
  };

  const columns = [
    {
      header: 'Rule',
      cell: (row) => {
        const Icon = ruleTypeIcons[row.rule_type] || Shield;
        return (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">{row.name}</p>
              <p className="text-xs text-muted-foreground">{ruleTypeLabels[row.rule_type]}</p>
            </div>
          </div>
        );
      }
    },
    {
      header: 'Value',
      cell: (row) => (
        <span className="font-mono text-lg font-bold text-primary">{row.value}</span>
      )
    },
    {
      header: 'Enforcement',
      cell: (row) => (
        <Badge variant="outline" className={row.is_blocking 
          ? 'bg-red-500/10 text-red-500 border-red-500/30' 
          : 'bg-amber-500/10 text-amber-500 border-amber-500/30'
        }>
          {row.is_blocking ? (
            <>
              <AlertTriangle className="h-3 w-3 mr-1" />
              Blocking
            </>
          ) : (
            'Warning Only'
          )}
        </Badge>
      )
    },
    {
      header: 'Description',
      cell: (row) => (
        <span className="text-sm text-muted-foreground max-w-[200px] truncate block">
          {row.description || '—'}
        </span>
      )
    },
    {
      header: 'Status',
      cell: (row) => (
        <Switch 
          checked={row.is_active}
          onCheckedChange={() => handleToggleActive(row.id, row.is_active)}
          data-testid={`toggle-rule-${row.id}`}
        />
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      <PageHeader 
        title="Compliance Rules"
        description="Configure SCHADS Award and NDIS compliance rules"
        actions={
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-rule-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Compliance Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Rule Name *</Label>
                  <Input 
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    placeholder="e.g., Maximum Overtime Hours"
                    data-testid="rule-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Rule Type *</Label>
                  <Select 
                    value={newRule.rule_type} 
                    onValueChange={(v) => setNewRule({...newRule, rule_type: v})}
                  >
                    <SelectTrigger data-testid="rule-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="min_break_hours">Minimum Break Between Shifts</SelectItem>
                      <SelectItem value="max_shift_hours">Maximum Shift Length</SelectItem>
                      <SelectItem value="max_weekly_hours">Maximum Weekly Hours</SelectItem>
                      <SelectItem value="required_qualification">Required Qualification</SelectItem>
                      <SelectItem value="staffing_ratio">Staffing Ratio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Value *</Label>
                  <Input 
                    type="number"
                    value={newRule.value}
                    onChange={(e) => setNewRule({...newRule, value: parseFloat(e.target.value)})}
                    data-testid="rule-value"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={newRule.description}
                    onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                    placeholder="Explain what this rule does..."
                    rows={2}
                    data-testid="rule-description"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                  <div>
                    <p className="font-medium">Blocking Rule</p>
                    <p className="text-sm text-muted-foreground">
                      Prevent shift creation when violated
                    </p>
                  </div>
                  <Switch 
                    checked={newRule.is_blocking}
                    onCheckedChange={(c) => setNewRule({...newRule, is_blocking: c})}
                    data-testid="rule-blocking"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-rule-btn">Create Rule</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Info Card */}
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            SCHADS Award Compliance
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>These rules help ensure compliance with the Social, Community, Home Care and Disability Services (SCHADS) Award:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Minimum 10 hours break between shifts</li>
            <li>Maximum 12 hours per shift (ordinary hours)</li>
            <li>Maximum 38 hours per week (ordinary hours)</li>
            <li>Overtime triggers after 38 hours/week or 10 hours/day</li>
          </ul>
        </CardContent>
      </Card>

      <DataTable 
        columns={columns}
        data={rules}
        emptyMessage="No compliance rules configured"
      />
    </div>
  );
};

export default CompliancePage;
